import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import { graphql } from 'gatsby'
import gsap from 'gsap'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import useMediaQuery from '../../../../hooks/useMediaQuery'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { publishViewCartEvent } from '../../../../lib/analytics/cart'
import { useCartContext } from '../../../../lib/cart'
import { ToggleCartContext } from '../../../../lib/cart/context'
import { totalQuantityExcludingDependents } from '../../../../lib/cart/item'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import {
  isAccountMenuEntry,
  isLoginMenuEntry,
  isRegistrationMenuEntry,
} from '../../../../utils/menuEntryTools'
import { filterMenuEntriesToDisplay } from '../../../../utils/sanity/documents/menuEntry'
import ScreenReaderOnly from '../../../ScreenReaderOnly'
import Nav from '../../../shop/account/Nav'
import SiteLogo from '../../SiteLogo'
import { MenuAccordionPropsItem } from '../MenuAccordion'
import { MenuAccordionToggle } from '../MenuAccordionToggle'
import MobileMenuEntryLink from '../MobileMenuEntryLink'
import { MobileMenuSubMenu } from '../MobileMenuSubMenu'
import { SearchContainer } from '../SearchContainer'
import SearchInput from '../SearchInput'
import * as Styled from './styled'

import closeIcon from '../../../../img/close.svg'
import menuIcon from '../../../../img/menu.svg'

interface MobileMenuModalProps {
  onCloseMenu: () => void
  showAccountMenu: boolean
}

export const AccountMenuNav = styled(Nav)<{ $isAccountPage: boolean }>`
  &.nav {
    padding: 16px 0 16px 8px;
  }
  .nav-link,
  .nav-link:visited {
    color: ${({ $isAccountPage, theme }) =>
      $isAccountPage ? theme.colors.grayscale.black : theme.colors.grayscale.white};
  }
`

const MobileMenuModal = React.forwardRef(function MobileMenuModal(
  { onCloseMenu, showAccountMenu }: MobileMenuModalProps,
  ref: React.Ref<{
    containerRef: React.Ref<HTMLDivElement>
    accordionListRef: React.Ref<HTMLDivElement>
  }>,
): React.ReactElement {
  const { mobileMenu: menuEntriesUnfiltered } = useSiteSettings() || {}
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const { isAuthenticated, isLoading } = useAuth0()
  const menuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered),
    [menuEntriesUnfiltered, shouldDisplayContent],
  )

  const navMenuItems = useMemo(() => {
    const filterMenuEntries = filterMenuEntriesToDisplay(shouldDisplayContent)
    return menuEntries.reduce(
      (acc: MenuAccordionPropsItem[], entry) => {
        if (!entry) return acc
        const { name: entryName } = entry
        const childEntries = filterMenuEntries(entry.childEntries)
        if (!entryName) return acc
        if (childEntries.length > 0) {
          acc.push({
            type: 'panel',
            key: entryName,
            toggleElement: <MenuAccordionToggle>{entryName}</MenuAccordionToggle>,
            collapseElement: (
              <Styled.TopLevelAccordionCollapse>
                <MobileMenuSubMenu menuEntries={childEntries} seeAllLink={entry.link} />
              </Styled.TopLevelAccordionCollapse>
            ),
          })
        } else if (isLoginMenuEntry(entry)) {
          acc.push({
            type: 'element',
            key: entryName,
            element:
              isAuthenticated && !isLoading ? (
                <Styled.MobileLogoutLink>Sign Out</Styled.MobileLogoutLink>
              ) : (
                <Styled.MobileLoginLink>{entryName}</Styled.MobileLoginLink>
              ),
          })
        } else if (isRegistrationMenuEntry(entry)) {
          acc.push({
            type: 'element',
            key: entryName,
            element: <Styled.MobileRegistrationLink>{entryName}</Styled.MobileRegistrationLink>,
          })
        } else if (!(isAccountMenuEntry(entry) && showAccountMenu)) {
          acc.push({
            type: 'element',
            key: entryName,
            element: <MobileMenuEntryLink link={entry.link}>{entryName}</MobileMenuEntryLink>,
          })
        }
        return acc
      },
      showAccountMenu
        ? [
            {
              type: 'panel',
              key: 'My Account',
              toggleElement: (
                <MenuAccordionToggle $isAccountPage>My Account Menu</MenuAccordionToggle>
              ),
              collapseElement: (
                <Styled.TopLevelAccordionCollapse>
                  <AccountMenuNav
                    className="account-menu-nav mobile-nav"
                    closeMenu={onCloseMenu}
                    $isAccountPage
                  />
                </Styled.TopLevelAccordionCollapse>
              ),
            },
          ]
        : [],
    )
  }, [isAuthenticated, isLoading, menuEntries, onCloseMenu, shouldDisplayContent, showAccountMenu])

  const { containerRef, accordionListRef } = (typeof ref === 'object' && ref?.current) || {
    containerRef: null,
    accordionListRef: null,
  }

  return (
    <Styled.NavContainer ref={containerRef}>
      <Styled.LogoLink to="/">
        <SiteLogo
          alt="Home"
          dfhLogoComponent={Styled.DfhLogoImage}
          dispensaryLogoComponent={Styled.DispensaryLogoImage}
        />
      </Styled.LogoLink>
      <Styled.CloseBox>
        <Styled.CloseButton onClick={onCloseMenu} type="button">
          <Styled.CloseIcon src={closeIcon} alt="Close" />
          <ScreenReaderOnly>Close navigation</ScreenReaderOnly>
        </Styled.CloseButton>
      </Styled.CloseBox>
      {navMenuItems.length > 0 && (
        <Styled.NavMenu
          role="tablist"
          activeKey={showAccountMenu ? 'My Account' : undefined}
          aria-multiselectable="true"
          ref={accordionListRef}
          items={navMenuItems}
        />
      )}
    </Styled.NavContainer>
  )
})

export interface MobileMenuProps {
  className?: string
  showAccountMenu: boolean
}

const MobileMenu: React.FC<MobileMenuProps> = ({ className, showAccountMenu }) => {
  const location = useLocation()
  const [inputValue, setInputValue] = useState(
    location?.href?.includes('search-results') ? location.search.split('=')[1] : '',
  )
  const [isInputActive, setIsInputActive] = useState(false)
  const { cart } = useCartContext()
  const { toggleCart: toggleCartModal } = useContext(ToggleCartContext)

  const tlRef = useRef<gsap.core.Timeline>()
  const containerRef = useRef<HTMLDivElement>(null)
  const accordionListRef = useRef<HTMLDivElement>(null)
  const overlightRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef({ containerRef, accordionListRef })

  const isNarrow = useMediaQuery({ maxWidth: '991px' })

  useEffect(() => {
    if (isNarrow) {
      tlRef.current = gsap.timeline()
    } else {
      tlRef.current = undefined
    }
  }, [isNarrow])

  const handleCartButtonClick = () => {
    toggleCartModal()
    if (cart) {
      publishViewCartEvent(cart)
    }
  }

  const openMenu = useCallback(() => {
    const tl = tlRef.current
    if (!tl || !containerRef.current || !overlightRef.current || !accordionListRef.current) {
      return
    }
    if (!tl.isActive()) {
      gsap.to(containerRef.current, { x: 0, duration: 0.35 })
      gsap.to(overlightRef.current, { autoAlpha: 1, duration: 0.35 })

      const listItems = Array.from(accordionListRef.current.children)
      tl.from(listItems, {
        left: -300,
        duration: 0.65,
        stagger: 0.02,
        ease: 'power3.out',
      })
    }
  }, [])

  const closeMenu = useCallback(() => {
    const tl = tlRef.current
    if (!tl || !containerRef.current || !overlightRef.current) {
      return
    }
    tl.clear()
      .add('close')
      .to(overlightRef.current, { autoAlpha: '0', duration: 0.35 }, 'close')
      .to(containerRef.current, { x: '-100%', duration: 0.35 }, 'close')
  }, [])

  const openSearch = useCallback(() => setIsInputActive(true), [])

  const closeSearch = useCallback(() => setIsInputActive(false), [])

  const totalQuantity = cart && totalQuantityExcludingDependents(cart.items, cart.total_quantity)
  const cartQuantityText = totalQuantity ? `${totalQuantity < 100 ? totalQuantity : '99+'}` : ''

  const searchInputRef = useRef<HTMLInputElement>(null)

  return (
    <div className={className} aria-hidden="true" data-cy="mobile-nav">
      <Styled.LogoLink to="/">
        <SiteLogo
          alt="Home"
          dfhLogoComponent={Styled.DfhLogoImage}
          dispensaryLogoComponent={Styled.DispensaryLogoImage}
        />
      </Styled.LogoLink>

      <Styled.MobileMenuButton onClick={openMenu} type="button">
        <Styled.MobileMenuIcon src={menuIcon} alt="Menu" />
        <ScreenReaderOnly>Show Navigation</ScreenReaderOnly>
      </Styled.MobileMenuButton>
      <Styled.MobileCartButton onClick={handleCartButtonClick} type="button">
        <Styled.MobileCartIcon quantityText={cartQuantityText} />
        <ScreenReaderOnly>Show cart</ScreenReaderOnly>
      </Styled.MobileCartButton>
      <Styled.MobileSearchInput>
        <SearchInput
          inputRef={searchInputRef}
          inputValue={inputValue}
          setInputValue={setInputValue}
          closeSearch={closeSearch}
          openSearch={openSearch}
          isActive={isInputActive}
          setIsActive={setIsInputActive}
        />
      </Styled.MobileSearchInput>
      {/* avoid rendering components on desktop */}
      {isNarrow && (
        <>
          {isInputActive && (
            <SearchContainer
              inputValue={inputValue}
              closeSearch={closeSearch}
              showSearch={isInputActive}
            />
          )}
          <Styled.NavOverlight ref={overlightRef} onMouseDown={closeMenu} />
          <MobileMenuModal
            onCloseMenu={closeMenu}
            ref={modalRef}
            showAccountMenu={showAccountMenu}
          />
        </>
      )}
    </div>
  )
}

const StyledMobileMenu = styled(MobileMenu)`
  --mobile-header-button-x: 17px;
  --mobile-header-button-y: 17px;
  --mobile-menu-padding-x: 1.6rem;
  position: absolute;
  display: block;
  top: 0;
  width: 100%;
  min-width: 320px;
  height: 122px;
  @media (min-width: 576px) {
    --mobile-header-button-x: 21.25px;
    --mobile-menu-padding-x: 3rem;
    height: 122px;
  }
  @media (min-width: 768px) {
    --mobile-header-button-x: 36px;
    --mobile-header-button-y: 24px;
    --mobile-menu-padding-x: 5.25rem;
    height: 158px;
  }
  @media (min-width: 992px) {
    display: none;
  }
  @media print {
    display: none;
  }
`

export { StyledMobileMenu as MobileMenu }

export const mobileMenu = graphql`
  fragment mobileMenuFragment on SanitySiteSettings {
    mobileMenu {
      ...MenuEntryFragment
      childEntries {
        ...MenuEntryFragment
        childEntries {
          ...MenuEntryFragment
          childEntries {
            ...MenuEntryFragment
          }
        }
      }
    }
  }
`
